import type Pusher from 'pusher-js'
import type { Channel } from 'pusher-js'

import { toPresence } from '~/data/graphql'
import { messageText } from '~/data/messageText'
import type { EventBus } from './bus'

type Json = Record<string, unknown>

function str(value: unknown): string | null {
  return typeof value === 'string' ? value : null
}

function firstString(obj: Json, ...keys: string[]): string | null {
  for (const key of keys) {
    const found = str(obj[key])
    if (found) return found
  }
  return null
}

function nestedId(obj: Json, key: string): string | null {
  const nested = obj[key]
  if (nested && typeof nested === 'object') {
    return str((nested as Json).id)
  }
  return null
}

function assigneeIds(data: Json): string[] {
  const raw =
    (Array.isArray(data.assignees) && data.assignees) ||
    (data.task &&
      typeof data.task === 'object' &&
      Array.isArray((data.task as Json).assignees) &&
      ((data.task as Json).assignees as unknown[])) ||
    []
  return (raw as unknown[])
    .map((entry) => {
      // The Laravel task broadcast sends assignees as plain user-id strings;
      // richer payloads send objects — accept both.
      if (typeof entry === 'string') return entry
      if (!entry || typeof entry !== 'object') return null
      const e = entry as Json
      return nestedId(e, 'user') ?? str(e.userId) ?? str(e.user_id) ?? str(e.id)
    })
    .filter((id): id is string => Boolean(id))
}

/**
 * Subscribe to the live channels that drive the world and translate raw Pusher
 * events into GameEvents. Events are routed by name *substring* (not exact
 * match) so the dot/namespace prefix Laravel applies can't silently break us.
 * Returns an unsubscribe function.
 */
export function subscribeWorld(params: {
  pusher: Pusher
  organizationId: string
  userId: string | null
  pulseIds: string[]
  roomPulseIds: string[]
  teamPulses: Array<{ pulseId: string; teamThreadId: string | null }>
  bus: EventBus
}): () => void {
  const { pusher, organizationId, userId, pulseIds, roomPulseIds, bus } = params
  const channels: Channel[] = []
  const roomPulseSet = new Set(roomPulseIds)

  // Private/presence subscriptions can fail their auth POST (token rotating,
  // network still waking from sleep) and pusher-js NEVER retries a failed
  // subscription until the next full reconnect — the public channels keep
  // working, so realtime dies PARTIALLY and silently. Retry with backoff.
  const retryTimers: number[] = []
  const withAuthRetry = (channel: Channel): Channel => {
    let tries = 0
    channel.bind('pusher:subscription_succeeded', () => {
      tries = 0
    })
    channel.bind('pusher:subscription_error', () => {
      if (tries >= 5) return
      tries += 1
      retryTimers.push(
        window.setTimeout(
          () => pusher.subscribe(channel.name),
          Math.min(30_000, 2_000 * 2 ** tries),
        ),
      )
    })
    return channel
  }

  const presence = withAuthRetry(pusher.subscribe('presence-users.presence'))
  const onPresenceChanged = (data: Json = {}): void => {
    const uid = firstString(data, 'user_id', 'userId')
    if (uid) {
      bus.emit({
        kind: 'presence',
        userId: uid,
        status: toPresence(firstString(data, 'presence', 'status')),
      })
    }
  }
  presence.bind('user-presence-changed', onPresenceChanged)
  presence.bind('.user-presence-changed', onPresenceChanged)
  const onMember = (status: 'online' | 'offline') => (data: Json = {}): void => {
    const info = (data.info as Json) ?? data
    const uid = firstString(info, 'id', 'user_id') ?? firstString(data, 'id')
    if (uid) bus.emit({ kind: 'presence', userId: uid, status })
  }
  presence.bind('pusher:member_added', onMember('online'))
  presence.bind('pusher:member_removed', onMember('offline'))
  channels.push(presence)

  // New team messages arrive on the logged-in user's OWN private channel: the
  // notification hub fans every team message out to each member — and the sender
  // — on private-users.{userId} as a `.team-message` event (see
  // services/notification-hub/handlers/team-message.mjs). We're logged in as a
  // member of these rooms, so this is the real, live feed for everything we see.
  if (userId) {
    const mine = withAuthRetry(pusher.subscribe('private-users.' + userId))
    // SPECIFIC binds (not bind_global) — this is how Laravel Echo / the dashboard
    // listen, and unlike bind_global they reliably fire for these dot-prefixed
    // hub events. The hub fans new messages here as `.team-message` (team/topic)
    // and `.direct-message` (1:1), plus reactions / typing / task-assigned.
    const onMessage = (data: Json = {}): void => {
      const pulseId = firstString(data, 'pulse_id', 'pulseId')
      if (!pulseId || !roomPulseSet.has(pulseId)) return
      bus.emit({
        kind: 'message',
        pulseId,
        userId: nestedId(data, 'sender') ?? firstString(data, 'user_id', 'userId'),
        // Hub payloads may carry raw Slate/JSON in `content` — flatten so
        // bubbles and the ticker never show JSON.
        text: messageText(firstString(data, 'preview', 'content', 'text')),
        topicId: firstString(data, 'topic_id', 'topicId'),
      })
    }
    mine.bind('.team-message', onMessage)
    mine.bind('.direct-message', onMessage)

    const onReaction = (data: Json = {}): void => {
      if (data.action === 'remove') return
      const uid = nestedId(data, 'user') ?? firstString(data, 'user_id', 'userId')
      if (uid) {
        bus.emit({
          kind: 'reaction',
          userId: uid,
          symbol: firstString(data, 'emoji', 'reaction', 'symbol') ?? '♥',
        })
      }
    }
    mine.bind('.team-reacted', onReaction)
    mine.bind('.dm-reacted', onReaction)
    mine.bind('.reaction', onReaction)

    mine.bind('.typing', (data: Json = {}): void => {
      if (data.is_typing === false) return
      const uid = nestedId(data, 'user') ?? firstString(data, 'user_id', 'userId')
      const pulseId = firstString(data, 'pulse_id', 'pulseId')
      if (uid && pulseId) bus.emit({ kind: 'typing', pulseId, userId: uid })
    })

    mine.bind('.task-assigned', (data: Json = {}): void => {
      const pulseId = firstString(data, 'pulse_id', 'pulseId')
      if (pulseId) {
        bus.emit({
          kind: 'task',
          action: 'updated',
          pulseId,
          taskId: firstString(data, 'task_id', 'taskId'),
          assigneeUserIds: [userId],
          actorId:
            firstString(data, 'actor_id', 'user_id') ??
            nestedId(data, 'actor') ??
            userId,
        })
      }
    })
    channels.push(mine)
  }

  for (const pulseId of pulseIds) {
    const channel = pusher.subscribe('pulse-task.' + pulseId)
    const emitTask =
      (action: 'created' | 'updated' | 'deleted') =>
      (data: Json = {}): void => {
        bus.emit({
          kind: 'task',
          action,
          pulseId,
          taskId:
            firstString(data, 'id', 'task_id', 'taskId') ?? nestedId(data, 'task'),
          assigneeUserIds: assigneeIds(data),
          actorId:
            firstString(data, 'actor_id', 'user_id', 'created_by', 'updated_by') ??
            nestedId(data, 'actor') ??
            nestedId(data, 'user'),
        })
      }
    // Bind both the Laravel broadcastAs name and the dot-prefixed variant.
    for (const ev of ['task.created', '.task.created']) channel.bind(ev, emitTask('created'))
    for (const ev of ['task.updated', '.task.updated']) channel.bind(ev, emitTask('updated'))
    for (const ev of ['task.deleted', '.task.deleted']) channel.bind(ev, emitTask('deleted'))
    channels.push(channel)

    // Notes: the pulse-task twin (BroadcastsNoteChanges, API #7471). Inert
    // until the API deploys it — the 60s shelf sweep remains the fallback.
    const noteChannel = pusher.subscribe('pulse-note.' + pulseId)
    const emitNote =
      (action: 'created' | 'updated' | 'deleted') =>
      (data: Json = {}): void => {
        bus.emit({
          kind: 'note',
          action,
          pulseId,
          noteId: firstString(data, 'note_id', 'noteId', 'id'),
          actorId: firstString(data, 'actor_id', 'user_id') ?? nestedId(data, 'actor'),
          title: firstString(data, 'title'),
        })
      }
    for (const ev of ['note.created', '.note.created']) noteChannel.bind(ev, emitNote('created'))
    for (const ev of ['note.updated', '.note.updated']) noteChannel.bind(ev, emitNote('updated'))
    for (const ev of ['note.deleted', '.note.deleted']) noteChannel.bind(ev, emitNote('deleted'))
    channels.push(noteChannel)
  }

  // Meeting sessions: the PUBLIC per-pulse channel the dashboard listens on
  // (LiveMeetingsContext). When a meeting starts/ends/pauses or recurring
  // sessions are created, emit a `meetingRefetch` so the world re-resolves from a
  // freshly-fetched snapshot (mirrors dashboard's REFETCH-on-event model). One
  // subscription per ROOM pulse (the rooms we actually draw); SPECIFIC binds.
  for (const pulseId of roomPulseSet) {
    const channel = pusher.subscribe(`meeting-session.${organizationId}.pulse.${pulseId}`)
    const onMeeting = (): void => {
      bus.emit({ kind: 'meetingRefetch', pulseId })
    }
    channel.bind('.meeting-session-status-updated', onMeeting)
    channel.bind('.recurring-meeting-sessions-created', onMeeting)
    channels.push(channel)
  }

  return () => {
    for (const timer of retryTimers) window.clearTimeout(timer)
    for (const channel of channels) {
      channel.unbind_global()
      channel.unbind()
      pusher.unsubscribe(channel.name)
    }
  }
}
